import { useState } from 'react'
import { useLocation, useParams } from 'react-router'
import { ArrowLeftRight, History } from 'lucide-react'
import {
  DateValue, DetailTabs, ErrorState, PageHeader, PermissionGate, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { EmployeeDocumentsTab } from '@/features/document'
import { useAssignments, useTransfers, useTransferMutations } from '../api'
import { AssignmentTimeline } from '../components/AssignmentTimeline'
import { TransferWizard, type TransferDraft } from '../components/TransferWizard'

/**
 * One employee's record: where they have sat, and what is on file for them.
 *
 * <p>Placement and documents are tabs on one screen rather than two routes, because they
 * answer each other's questions — a transfer is refused when a document has lapsed, and the
 * first thing anybody does about that refusal is look at the documents.
 */
export function EmployeeAssignmentsPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const [wizardOpen, setWizardOpen] = useState(false)
  // The tree knows this route as a leaf under Employees; the person's name is appended
  // because only the page can know it.
  const breadcrumb = useBreadcrumb('Record')

  // Both /assignments and /documents render this page, so the opening tab is read from the
  // URL rather than defaulted. A page that ignores its own route is how the attendance
  // screen ended up rendering Devices for three different links.
  const initialTab = location.pathname.endsWith('/documents') ? 'documents' : 'placement'

  const assignments = useAssignments(id)
  const transfers = useTransfers(id)
  const actions = useTransferMutations()

  const departments = useLookup<{ id: string; name: string }>('departments')
  const locations = useLookup<{ id: string; name: string }>('locations')
  const designations = useLookup<{ id: string; name: string }>('designations')
  const grades = useLookup<{ id: string; name: string }>('grades')
  const costCenters = useLookup<{ id: string; name: string }>('costCenters')

  const current = assignments.data?.find((assignment) => assignment.current)
  // The assignment endpoint deliberately does not repeat the employee's name on every row;
  // the transfer list carries it, and the wizard only needs it for its confirmation line.
  const employeeName = transfers.data?.find((transfer) => transfer.employeeName)?.employeeName

  function raise(draft: TransferDraft) {
    actions.create.mutate(draft as unknown as Record<string, unknown>, {
      onSuccess: (created) => {
        // Raised and sent in one go: a draft nobody submits is indistinguishable from a
        // transfer that was never raised, and the approver is the point.
        actions.submit.mutate(created.id, { onSuccess: () => setWizardOpen(false) })
      },
    })
  }

  const placement = (
    <div className="space-y-4">
      <PermissionGate permission="employee:transfer">
        {!wizardOpen && (
          <div className="flex justify-end">
            <Button onClick={() => setWizardOpen(true)}>
              <ArrowLeftRight aria-hidden="true" />
              Transfer
            </Button>
          </div>
        )}
      </PermissionGate>

      {wizardOpen && id && (
        <TransferWizard
          employeeId={id}
          employeeName={employeeName ?? 'This employee'}
          current={current}
          departments={asOptions(departments.data)}
          locations={asOptions(locations.data)}
          designations={asOptions(designations.data)}
          grades={asOptions(grades.data)}
          costCenters={asOptions(costCenters.data)}
          submitting={actions.create.isPending || actions.submit.isPending}
          error={actions.create.error ?? actions.submit.error}
          onSubmit={raise}
          onCancel={() => setWizardOpen(false)}
        />
      )}

      {transfers.data != null && transfers.data.length > 0 && (
        <Card>
          <h2 className="border-b border-[var(--color-border)] px-4 py-3 text-sm font-semibold text-[var(--color-heading)]">
            Transfers
          </h2>
          <ul className="divide-y divide-[var(--color-border)]">
            {transfers.data.map((transfer) => (
              <li key={transfer.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
                <span className="min-w-0 flex-1 text-[var(--color-heading)]">
                  {transfer.toDepartmentName ?? transfer.toDesignationName ?? 'Placement change'}
                  {transfer.transferType === 'TEMPORARY' && (
                    <span className="ms-2 text-xs text-[var(--color-muted)]">temporary</span>
                  )}
                </span>
                <span className="shrink-0 text-[var(--color-body)]">
                  <DateValue value={transfer.effectiveFrom} />
                </span>
                <StatusBadge status={transfer.status} />
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div>
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-[var(--color-heading)]">
          <History className="size-4" aria-hidden="true" />
          History
        </h2>
        {assignments.isLoading && <Skeleton className="h-64 w-full rounded-[var(--radius-card)]" />}
        {assignments.error != null && (
          <ErrorState error={assignments.error} onRetry={() => void assignments.refetch()} />
        )}
        {assignments.data != null && <AssignmentTimeline assignments={assignments.data} />}
      </div>
    </div>
  )

  return (
    <div className="space-y-4">
      <PageHeader
        title="Employee record"
        subtitle="Where this person has sat, and what is on file for them"
        breadcrumb={breadcrumb}
      />

      <DetailTabs
        defaultTab={initialTab}
        tabs={[
          { key: 'placement', label: 'Placement', content: placement },
          {
            key: 'documents',
            label: 'Documents',
            content: id ? <EmployeeDocumentsTab employeeId={id} /> : null,
          },
        ]}
      />
    </div>
  )
}

/** The lookup lists all expose id + name; the wizard only needs those two. */
function asOptions(rows: unknown[] | undefined): { id: string; name: string }[] {
  if (!rows) {
    return []
  }
  return rows
    .map((row) => row as { id?: string; name?: string })
    .filter((row): row is { id: string; name: string } => Boolean(row.id && row.name))
    .map((row) => ({ id: row.id, name: row.name }))
}
